import { pageUrl } from '@constants/url';
import {
  Ban,
  BarChart3,
  Bell,
  Boxes,
  CirclePlus,
  ClipboardCheck,
  CreditCard,
  Crown,
  Database,
  DollarSign,
  FileArchive,
  FileInput,
  Forward,
  Gamepad2,
  GraduationCap,
  Landmark,
  List,
  Mail,
  Newspaper,
  Radar,
  Reply,
  Scissors,
  Settings,
  Shield,
  Ship,
  Stamp,
  Table2,
  Truck,
  UsersRound,
  Warehouse,
  Building2,
  Factory,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { impersonationUtils } from '@utils/impersonation';
import { isSuperAdminEmail } from '@utils/access';
import { isProfessionalModeEnabled } from '@utils/professionalMode';
import { useCompanyFeatures } from '@contexts/CompanyFeaturesContext';
import { usePermissions } from '@contexts/PermissionsContext';
import { BILLING_ENABLED } from '@constants/features';

const iconClass = 'shrink-0';
const navIcon = (Icon: React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>) => (
  <Icon className={iconClass} size={16} strokeWidth={1.5} />
);

// Emails that are view-only for Kepabeanan (no edit in both BC 2.0 and BC 3.0)
const KEPABEANAN_VIEW_ONLY_EMAILS = [
  'anne.veronica@molgroup.com',
  'ikumi.shibata1@molgroup.com',
  'panji@gateway-id.com',
];

// Emails that are view-only for Manifest
const MANIFEST_VIEW_ONLY_EMAILS = [
  'panji@gateway-id.com',
  'anne.veronica@molgroup.com',
  'ikumi.shibata1@molgroup.com',
  'christina.vonny@molgroup.com',
  'ari.wibowo@molgroup.com',
  'femy.muthia@molgroup.com',
  'reza.haqqa@molgroup.com',
  'ratri.puji@molgroup.com',
  'mlg-id.airexport01@molgroup.com',
];

const menus = [
  {
    label: 'Mgmt Dashboard',
    i18nKey: 'menu.mgmtDashboard',
    icon: navIcon(Crown),
    link: pageUrl.managementDashboard,
    badge: 'BOD',
  },
  {
    label: 'Manifest',
    i18nKey: 'menu.manifest',
    icon: navIcon(Ship),
    link: '#',
    subLinks: [
      {
        label: 'Dashboard',
        i18nKey: 'menu.dashboard',
        icon: navIcon(BarChart3),
        link: '/manifest/dashboard',
        isMinimized: false,
      },
      {
        label: 'Buat Aju',
        i18nKey: 'menu.buatAju',
        icon: navIcon(CirclePlus),
        link: '/manifest/product',
        isMinimized: false,
      },
      {
        label: 'Draft',
        i18nKey: 'menu.draft',
        icon: navIcon(List),
        link: '/manifest/draft',
        isMinimized: false,
      },
      {
        label: 'Doc Manifes',
        i18nKey: 'menu.docManifes',
        icon: navIcon(FileInput),
        link: '/manifest/doc-pabean',
        isMinimized: false,
      },
      {
        label: 'The Guardian',
        i18nKey: 'menu.theGuardian',
        icon: navIcon(Bell),
        link: '/manifest/guardian',
        isMinimized: false,
      },
      {
        label: 'Radar',
        i18nKey: 'menu.radar',
        icon: navIcon(Radar),
        link: '/manifest/guardian/radar',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Kepabeanan',
    i18nKey: 'menu.kepabeanan',
    icon: navIcon(FileInput),
    link: '#',
    subLinks: [
      {
        label: 'Dashboard',
        i18nKey: 'menu.dashboard',
        icon: navIcon(BarChart3),
        link: pageUrl.dashboard,
        isMinimized: false,
      },
      {
        label: 'Buat Aju',
        i18nKey: 'menu.buatAju',
        icon: navIcon(CirclePlus),
        link: '/buat-aju',
        isMinimized: false,
      },
      {
        label: 'Doc Pabean',
        i18nKey: 'menu.docPabean',
        icon: navIcon(FileInput),
        link: pageUrl.docPabean,
        isMinimized: false,
      },
      {
        label: 'Monitoring Blokir',
        i18nKey: 'menu.monitoringBlokir',
        icon: navIcon(Ban),
        link: '/monitoring-blokir',
        isMinimized: false,
      },
      {
        label: 'Draft',
        i18nKey: 'menu.draft',
        icon: navIcon(List),
        link: pageUrl.draftDocPabean,
        isMinimized: false,
      },
      {
        label: 'Respon',
        i18nKey: 'menu.respon',
        icon: navIcon(Reply),
        link: '/respon',
        isMinimized: false,
      },
      {
        label: 'Database Barang',
        i18nKey: 'menu.databaseBarang',
        icon: navIcon(Database),
        link: '/database-barang',
        isMinimized: false,
      },
      {
        label: 'PPFTZ',
        i18nKey: 'menu.ppftz',
        icon: navIcon(Warehouse),
        link: pageUrl.ppftz.list,
        isMinimized: false,
        badge: 'New',
      },
    ],
  },
  {
    label: 'Kuota Impor / Ekspor',
    i18nKey: 'menu.kuotaImporEkspor',
    icon: navIcon(Table2),
    link: '#',
    subLinks: [
      {
        label: 'Shipment Quota',
        i18nKey: 'menu.shipmentQuota',
        icon: navIcon(Table2),
        link: '/shipment-quota',
        isMinimized: false,
      },
      {
        label: 'Harga Referensi',
        i18nKey: 'menu.hargaReferensi',
        icon: navIcon(DollarSign),
        link: '/harga-referensi',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Cukai',
    i18nKey: 'menu.cukai',
    icon: navIcon(Stamp),
    link: '#',
    badge: 'New',
    subLinks: [
      {
        label: 'Tutorial',
        i18nKey: 'menu.tutorial',
        icon: navIcon(GraduationCap),
        link: '/cukai/tutorial',
        isMinimized: false,
        badge: 'Mulai',
      },
      {
        label: 'Surat Jalan',
        i18nKey: 'menu.suratJalan',
        icon: navIcon(Truck),
        link: '/cukai/surat-jalan',
        isMinimized: false,
      },
      {
        label: 'Dokumen CK',
        i18nKey: 'menu.dokumenCk',
        icon: navIcon(Stamp),
        link: '/cukai/documents',
        isMinimized: false,
      },
      {
        label: 'Bill of Material',
        i18nKey: 'menu.billOfMaterial',
        icon: navIcon(Boxes),
        link: '/cukai/bom',
        isMinimized: false,
      },
      {
        label: 'Inventori',
        i18nKey: 'menu.inventori',
        icon: navIcon(Warehouse),
        link: '/cukai/inventory',
        isMinimized: false,
      },
      {
        label: 'Risk Self-Check',
        i18nKey: 'menu.riskSelfCheck',
        icon: navIcon(Shield),
        link: '/cukai/compliance',
        isMinimized: false,
        badge: 'AI Ready',
      },
    ],
  },
  {
    label: 'Customer Approval',
    i18nKey: 'menu.customerApproval',
    icon: navIcon(ClipboardCheck),
    link: '#',
    subLinks: [
      {
        label: 'Approval Requests',
        i18nKey: 'menu.approvalRequests',
        icon: navIcon(ClipboardCheck),
        link: '/customer-approval',
        isMinimized: false,
      },
      {
        label: 'Email Groups',
        i18nKey: 'menu.emailGroups',
        icon: navIcon(List),
        link: '/customer-approval/email-groups',
        isMinimized: false,
      },
      {
        label: 'Reviewers',
        i18nKey: 'menu.reviewers',
        icon: navIcon(UsersRound),
        link: '/customer-approval/reviewers',
        isMinimized: false,
      },
      {
        label: 'Roles & Permissions',
        i18nKey: 'menu.rolesPermissions',
        icon: navIcon(Shield),
        link: '/customer-approval/roles',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Credit Topup',
    i18nKey: 'menu.creditTopup',
    icon: navIcon(CreditCard),
    link: '/credit/topup',
  },
  {
    label: 'Langganan',
    i18nKey: 'menu.langganan',
    icon: navIcon(Crown),
    link: pageUrl.subscription,
  },
  {
    label: 'Berita',
    i18nKey: 'menu.berita',
    icon: navIcon(Newspaper),
    link: '#',
    subLinks: [
      {
        label: 'Antara News',
        i18nKey: 'menu.antaraNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=antara',
        isMinimized: false,
      },
      {
        label: 'CNN Indonesia',
        i18nKey: 'menu.cnnIndonesia',
        icon: navIcon(Newspaper),
        link: '/berita?source=cnn',
        isMinimized: false,
      },
      {
        label: 'CNBC Indonesia',
        i18nKey: 'menu.cnbcIndonesia',
        icon: navIcon(Newspaper),
        link: '/berita?source=cnbc',
        isMinimized: false,
      },
      {
        label: 'Republika News',
        i18nKey: 'menu.republikaNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=republika',
        isMinimized: false,
      },
      {
        label: 'Kumparan News',
        i18nKey: 'menu.kumparanNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=kumparan',
        isMinimized: false,
      },
      {
        label: 'VOA News',
        i18nKey: 'menu.voaNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=voa',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Layanan Lainnya',
    i18nKey: 'menu.layananLainnya',
    icon: navIcon(Settings),
    link: '#',
    subLinks: [
      {
        label: 'Stamp Duty',
        i18nKey: 'menu.stampDuty',
        icon: navIcon(FileInput),
        link: pageUrl.stampDuty,
        isMinimized: false,
      },
      {
        label: 'Mini Games',
        i18nKey: 'menu.miniGames',
        icon: navIcon(Gamepad2),
        link: '/mini-games',
        isMinimized: false,
        badge: 'New',
      },
      {
        label: 'File Compress',
        i18nKey: 'menu.fileCompress',
        icon: navIcon(FileArchive),
        link: pageUrl.fileCompression,
        isMinimized: false,
      },
      {
        label: 'Split PDF',
        i18nKey: 'menu.splitPdf',
        icon: navIcon(Scissors),
        link: '/split-pdf',
        isMinimized: false,
        badge: 'New',
      },
      {
        label: 'Email Groups',
        i18nKey: 'menu.emailGroups',
        icon: navIcon(ClipboardCheck),
        link: '/customer-approval/email-groups',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Master',
    i18nKey: 'menu.master',
    icon: navIcon(Database),
    link: '#',
    subLinks: [
      {
        label: 'Ceisa User',
        i18nKey: 'menu.ceisaUser',
        icon: navIcon(UsersRound),
        link: '/master/ceisa-user',
        isMinimized: false,
      },
      {
        label: 'User',
        i18nKey: 'menu.user',
        icon: navIcon(UsersRound),
        link: '/master/user',
        isMinimized: false,
      },
      {
        label: 'Sea Carriers',
        i18nKey: 'menu.seaCarriers',
        icon: navIcon(Ship),
        link: '/master/sea-carriers',
        isMinimized: false,
      },
      {
        label: 'Export Bank',
        i18nKey: 'menu.exportBank',
        icon: navIcon(Landmark),
        link: '/master/export-bank',
        isMinimized: false,
      },
      {
        label: 'Email',
        i18nKey: 'menu.email',
        icon: navIcon(Mail),
        link: '/master/email',
        isMinimized: false,
      },
      {
        label: 'Customer Email Forwarding',
        i18nKey: 'menu.customerEmailForwarding',
        icon: navIcon(Forward),
        link: pageUrl.customerEmailForwarding,
        isMinimized: false,
      },
      {
        label: 'Statement',
        i18nKey: 'menu.statement',
        icon: navIcon(ClipboardCheck),
        link: '/master/statement',
        isMinimized: false,
      },
      {
        label: 'Data Consignee',
        i18nKey: 'menu.dataConsignee',
        icon: navIcon(Building2),
        link: '/master/consignee',
        isMinimized: false,
      },
      {
        label: 'Data Eksportir',
        i18nKey: 'menu.dataEksportir',
        icon: navIcon(Factory),
        link: '/master/eksportir',
        isMinimized: false,
      },
      {
        label: 'Manifest Reminder',
        i18nKey: 'menu.manifestReminder',
        icon: navIcon(Bell),
        link: '/master/manifest-reminder',
        isMinimized: false,
      },
      {
        label: 'Revoke Access',
        i18nKey: 'menu.revokeAccess',
        icon: navIcon(Ban),
        link: '/master/revoke-access',
        isMinimized: false,
      },
    ],
  },
];

/**
 * Every navigable deep link the sidebar knows about, flattened.
 *
 * MenuAccessGuard checks a directly-typed URL against this catalog so route
 * access and sidebar visibility are decided from the same source: if a path
 * belongs to a menu entry the user's role did not grant, the page is blocked
 * rather than merely hidden from the nav.
 */
export const ALL_MENU_LINKS: string[] = menus
  .flatMap((item) => [
    item.link,
    ...((item as { subLinks?: { link?: string }[] }).subLinks ?? []).map((sub) => sub.link),
  ])
  .filter((link): link is string => typeof link === 'string' && link !== '' && link !== '#')
  .map((link) => link.split('?')[0]);

/* ------------------------------------------------------------------ *
 *  Shared pieces
 *
 *  The rail and the onboarding film (src/features/first-login) render the
 *  same identity, the same menu filtering, the same surface and the same
 *  header. They live here, next to `menus`, so the film cannot drift from
 *  what the user actually sees.
 * ------------------------------------------------------------------ */

/** Who is looking at the rail: email, impersonation and professional mode, kept in sync with storage. */
export const useSidebarIdentity = () => {
  const [userEmail, setUserEmail] = useState<string>('');
  const [canImpersonate, setCanImpersonate] = useState<boolean>(false);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [professionalModeEnabled, setProfessionalModeEnabled] = useState<boolean>(false);

  const updateUserInfo = () => {
    const authData = localStorage.getItem('authData');
    const impersonating = impersonationUtils.isImpersonating();

    setIsImpersonating(impersonating);

    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        const email = parsed.email || '';
        setUserEmail(email);

        // For impersonation permissions, check original user's email when impersonating
        if (impersonating) {
          const originalAuthData = localStorage.getItem('originalAuthData');
          if (originalAuthData) {
            const originalParsed = JSON.parse(originalAuthData);
            const originalEmail = originalParsed.email || '';
            setCanImpersonate(impersonationUtils.canImpersonate(originalEmail));
          } else {
            setCanImpersonate(false);
          }
        } else {
          // When not impersonating, use current user's email for permissions
          setCanImpersonate(impersonationUtils.canImpersonate(email));
        }
      } catch (error) {
        console.error('Error parsing auth data:', error);
        setUserEmail('');
        setCanImpersonate(false);
      }
    } else {
      setUserEmail('');
      setCanImpersonate(false);
    }
  };

  useEffect(() => {
    updateUserInfo();

    // Listen for storage changes to update UI when impersonation state changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authData' || e.key === 'impersonationSession' || e.key === 'originalAuthData') {
        updateUserInfo();
      }
    };

    // Listen for custom events from impersonation actions
    const handleImpersonationChange = () => {
      updateUserInfo();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('impersonationChanged', handleImpersonationChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  useEffect(() => {
    const updateProfessionalMode = () => {
      setProfessionalModeEnabled(isProfessionalModeEnabled());
    };

    updateProfessionalMode();

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', updateProfessionalMode);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', updateProfessionalMode);
      }
    };
  }, []);

  return { userEmail, canImpersonate, isImpersonating, professionalModeEnabled };
};

/** The menu list exactly as the rail shows it for this user, plus the module lock lookup. */
export const useSidebarMenus = ({
  userEmail,
  professionalModeEnabled,
  searchQuery = '',
}: {
  userEmail: string;
  professionalModeEnabled: boolean;
  searchQuery?: string;
}) => {
  const displayedMenus = useMemo(() => {
    const email = userEmail.toLowerCase();
    const isKepabeananViewOnly = KEPABEANAN_VIEW_ONLY_EMAILS.includes(email);
    const isManifestViewOnly = MANIFEST_VIEW_ONLY_EMAILS.includes(email);

    let result = menus;

    // Billing (Credit Topup + Langganan) is hidden by build flag, not by role:
    // the routes themselves redirect to /dashboard in App.tsx.
    if (!BILLING_ENABLED) {
      result = result.filter((item) => item.label !== 'Credit Topup' && item.label !== 'Langganan');
    }

    if (professionalModeEnabled) {
      result = result.map((item) => {
        if (item.label !== 'Layanan Lainnya' || !item.subLinks) {
          return item;
        }
        return {
          ...item,
          subLinks: item.subLinks.filter((subItem) => subItem.label !== 'Stamp Duty' && subItem.label !== 'Mini Games'),
        };
      });
    }

    // For non-super-admin users, filter Master subLinks to only show User and Email
    if (!isSuperAdminEmail(email)) {
      result = result.map((item) => {
        if (item.label !== 'Master' || !item.subLinks) return item;
        return {
          ...item,
          subLinks: item.subLinks.filter(
            (sub) =>
              sub.label === 'User' ||
              sub.label === 'Email' ||
              sub.label === 'Customer Email Forwarding' ||
              sub.label === 'Statement' ||
              sub.label === 'Data Consignee' ||
              sub.label === 'Data Eksportir' ||
              sub.label === 'Manifest Reminder' ||
              sub.label === 'Export Bank'
          ),
        };
      });
    }

    // Hide "Buat Aju" for view-only users
    if (isKepabeananViewOnly || isManifestViewOnly) {
      result = result.map((item) => {
        if (!item.subLinks) return item;

        if (item.label === 'Kepabeanan' && isKepabeananViewOnly) {
          return {
            ...item,
            subLinks: item.subLinks.filter((sub) => sub.label !== 'Buat Aju'),
          };
        }
        if (item.label === 'Manifest' && isManifestViewOnly) {
          return {
            ...item,
            subLinks: item.subLinks.filter((sub) => sub.label !== 'Buat Aju'),
          };
        }
        return item;
      });
    }

    return result;
  }, [professionalModeEnabled, userEmail]);

  const roleFilteredMenus = useMemo(() => {
    return displayedMenus.filter((item) => {
      if (userEmail.toLowerCase().includes('samuel')) {
        return item.label === 'Dashboard';
      }
      if (userEmail.toLowerCase().includes('fedex.com') && item.label === 'Mgmt Dashboard') {
        return false;
      }
      if (userEmail.toLowerCase().includes('demo')) {
        return item.label !== 'SSM' && item.label !== 'Stamp Duty';
      }
      if (userEmail.toLowerCase().includes('doc%')) {
        return item.label !== 'Kepabeanan';
      }
      if (item.label === 'Master') {
        if (isSuperAdminEmail(userEmail)) {
          return true;
        }
        if (
          userEmail.toLowerCase().includes('nissinti') &&
          userEmail.toLowerCase() !== 'ibnu.wardhana@nissinti.co.id'
        ) {
          return false;
        }
        return true;
      }
      return true;
    });
  }, [displayedMenus, userEmail]);

  // Custom-role menu access. `canAccessMenu` fails open only for users still
  // on their legacy role, so the sidebar narrows as soon as an admin assigns a
  // custom role — including one that grants no menus at all.
  const { canAccessMenu } = usePermissions();

  const permissionFilteredMenus = useMemo(() => {
    return roleFilteredMenus
      .map((item) => {
        if (!item.subLinks) return item;
        const subLinks = item.subLinks.filter((sub) => canAccessMenu(sub.link));
        return { ...item, subLinks };
      })
      .filter((item) => {
        // A group survives if any of its children survived; a plain link is
        // checked directly.
        if (item.subLinks) return item.subLinks.length > 0;
        return canAccessMenu(item.link);
      });
  }, [roleFilteredMenus, canAccessMenu]);

  // Per-company module locks: locked groups stay visible but dimmed, and
  // clicking them opens the contact-admin dialog instead of navigating.
  const { isFeatureEnabled } = useCompanyFeatures();

  const filteredMenus = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return permissionFilteredMenus;

    return permissionFilteredMenus
      .map((item) => {
        const labelMatch = item.label.toLowerCase().includes(q);
        if (!item.subLinks) {
          return labelMatch ? item : null;
        }
        const matchedSubs = item.subLinks.filter((sub) => sub.label.toLowerCase().includes(q));
        if (labelMatch) return item;
        if (matchedSubs.length > 0) {
          return { ...item, subLinks: matchedSubs };
        }
        return null;
      })
      .filter(Boolean) as typeof permissionFilteredMenus;
  }, [permissionFilteredMenus, searchQuery]);

  return { filteredMenus, isFeatureEnabled };
};
